import { useEffect, useMemo, useState } from "react";
import { useAnimals } from "../../../hooks/useAnimals";
import {
  getDisplayKupeNo,
  getDisplaySiparisNo,
} from "../../../lib/animalLabels";
import {
  getSlaughterOps,
  rehydrateSlaughterOps,
  subscribeSlaughterOps,
  type SlaughterOpRecord,
  type SlaughterOpStatus,
} from "../../../lib/slaughterOpsStore";

type TvStatus = Exclude<SlaughterOpStatus, "done">;

interface TvRow {
  animalId: number;
  kupeNo: number;
  kupeNoLabel: string;
  siparisNo: string;
  tur: string;
  status: TvStatus;
  eventAt?: string;
  packingZone?: number | null;
}

const TITLES: Record<TvStatus, string> = {
  queued: "Sıra Bekliyor",
  entered: "Kesime Girdi",
  cut: "Kesim Yapıldı",
  packing: "Parçalanıyor",
};

function toNumber(value: string) {
  const parsed = Number(String(value ?? "").replace(/[^\d]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
}

function getStatusEventAt(status: TvStatus, op?: SlaughterOpRecord) {
  if (status === "entered") return op?.startedAt ?? op?.updatedAt ?? op?.queuedAt;
  if (status === "cut") return op?.cutAt ?? op?.updatedAt ?? op?.startedAt;
  if (status === "packing") return op?.packedAt ?? op?.updatedAt ?? op?.cutAt;
  return op?.queuedAt ?? op?.updatedAt;
}

function eventTime(value?: string) {
  if (!value) return Number.POSITIVE_INFINITY;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
}

function sortTvRows(rows: TvRow[]) {
  return [...rows].sort((a, b) => {
    const byEvent = eventTime(a.eventAt) - eventTime(b.eventAt);
    if (byEvent !== 0) return byEvent;
    return a.kupeNo - b.kupeNo;
  });
}

export default function TvEkraniPage() {
  const { animals } = useAnimals();
  const [version, setVersion] = useState(0);

  useEffect(() => {
    void rehydrateSlaughterOps();

    return subscribeSlaughterOps(() => {
      setVersion((value) => value + 1);
    });
  }, []);

  const rows = useMemo<TvRow[]>(() => {
    const opMap = new Map(getSlaughterOps().map((item) => [item.animalId, item]));

    return animals
      .filter((animal) => {
        const op = opMap.get(animal.id);
        if (!animal.kesimeUygun && !op) return false;
        if (!animal.kesimeUygun && op?.status === "queued" && op?.hidden) return false;
        if (op?.status === "done") return false;
        return true;
      })
      .map((animal) => {
        const op = opMap.get(animal.id);
        const rawStatus = (op?.status ?? "queued") as SlaughterOpStatus;
        const status = rawStatus === "done" ? "packing" : (rawStatus as TvStatus);

        return {
          animalId: animal.id,
          kupeNo: toNumber(getDisplayKupeNo(animal)),
          kupeNoLabel: getDisplayKupeNo(animal),
          siparisNo: getDisplaySiparisNo(animal),
          tur: animal.tur,
          status,
          eventAt: getStatusEventAt(status, op),
          packingZone:
            rawStatus === "packing" ? Number(op?.packingZone ?? 0) || null : null,
        };
      })
      .filter((row) => Number.isFinite(row.kupeNo) && row.kupeNo > 0);
  }, [animals, version]);

  const columns = useMemo(() => {
    const statuses: TvStatus[] = ["queued", "entered", "cut", "packing"];

    return statuses.map((status) => ({
      status,
      title: TITLES[status],
      rows: sortTvRows(rows.filter((row) => row.status === status)),
    }));
  }, [rows]);

  return (
    <div className="min-h-screen bg-[#f6f1e7] px-3 py-4 font-body">
      <div className="max-w-[1850px] mx-auto space-y-3">
        <div className="bg-white rounded-[18px] border border-[#eadfcf] px-4 py-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h1 className="font-heading text-xl font-bold text-[#5d4631]">
                Evren Çiftliği TV Ekranı
              </h1>
              <p className="text-[11px] text-[#8f7a68] mt-1">
                Tamamlananlar burada görünmez. Ekran küpe numarasına göre değil, operasyon olay sırasına göre akar.
              </p>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {columns.map((column) => (
                <div
                  key={column.status}
                  className="px-2.5 py-1.5 rounded-full bg-[#f6f1e7] border border-[#eadfcf] text-[11px] font-semibold text-[#5d4631]"
                >
                  {column.title}: {column.rows.length}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid xl:grid-cols-4 md:grid-cols-2 gap-3">
          {columns.map((column) => (
            <section
              key={column.status}
              className="bg-white rounded-[18px] border border-[#eadfcf] overflow-hidden"
            >
              <div className="px-3 py-2 border-b border-[#eadfcf] bg-[#fbf7f0]">
                <div className="flex items-center justify-between gap-2">
                  <h2 className="font-heading text-xs font-bold uppercase tracking-wide text-[#5d4631]">
                    {column.title}
                  </h2>
                  <span className="inline-flex items-center justify-center min-w-[26px] h-6 px-2 rounded-full bg-white border border-[#eadfcf] text-[10px] font-semibold text-[#8f7a68]">
                    {column.rows.length}
                  </span>
                </div>
              </div>

              <div className="p-2 space-y-2 max-h-[76vh] overflow-y-auto">
                {column.rows.length === 0 ? (
                  <div className="py-8 text-center text-[11px] text-[#b3a08f]">
                    Bu kolonda kayıt yok
                  </div>
                ) : (
                  column.rows.map((row) => (
                    <div
                      key={row.animalId}
                      className="rounded-[14px] border border-[#eadfcf] bg-[#fffdf9] px-2.5 py-2"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-lg font-black text-[#5d4631] leading-none">
                              {row.kupeNoLabel}
                            </span>
                            <span className="inline-flex items-center rounded-full border border-[#eadfcf] bg-white px-2 py-0.5 text-[10px] font-semibold text-[#8f7a68]">
                              {row.tur}
                            </span>
                            {row.status === "packing" && row.packingZone && (
                              <span className="inline-flex items-center rounded-full border border-[#f1d7a7] bg-[#fff4db] px-2 py-0.5 text-[10px] font-semibold text-[#b67b12]">
                                Parçalama {row.packingZone}
                              </span>
                            )}
                          </div>

                          <div className="mt-1 text-[11px] text-[#8f7a68] leading-4">
                            <span className="font-medium">Sipariş:</span> {row.siparisNo}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
